import { Decimal } from "decimal.js";
import { round2, roundMoney, toNumber, TAX_ROUND } from "./rounding";

/**
 * Single pricing engine for the POS order domain.
 *
 * All money math should flow through this module. Functions here intentionally
 * take plain data + a `settings` object and never read the Vuex store, so the
 * engine is pure and unit-testable.
 *
 * `settings` shape (all optional):
 *   {
 *     isTaxInclusive: boolean,            // config/isTaxInclusive
 *     taxRound: "nearest"|"no"|"order_level_distributed", // store.tax_round
 *     rewardsConversionRate: number,      // $ value of one reward point (default 0.01)
 *   }
 */

const POINTS_ITEM_NAME = "Points Redeemed";

// ─── Item-level helpers (mirror helpers/calc.js, Decimal HALF_UP) ─────────────

/**
 * CANONICAL modifier price rule (Phase 3).
 *
 * Before Phase 3 the codebase had THREE disagreeing modifier-sum conventions:
 *   1. getModSum          -> price_original ?? price   (0 kept)
 *   2. getItemSubtotal    -> price_original || price   (0 falls back to price)
 *   3. points block       -> price_original || 0       (price-only mods ignored)
 *
 * These only diverged for the (in practice non-existent) case of a modifier
 * with `price_original === 0` AND a separate non-zero `price`, and for legacy
 * payloads that carry only `price`. We collapse them into one rule:
 *
 *   effective price = price_original when it is a real number (incl. 0),
 *                     else price, else 0.
 *
 * Modifiers are always created with both fields set together
 * (mixins/order/modifier: price_original = menu price, price = tax-adjusted),
 * so `price_original === 0` legitimately means "free" and is authoritative.
 * `price` is only used as a fallback for older/server data that omitted
 * price_original.
 */
export function getModifierEffectivePrice(mod) {
  if (!mod) return 0;
  if (mod.price_original !== undefined && mod.price_original !== null) {
    return toNumber(mod.price_original);
  }
  return toNumber(mod.price);
}

/**
 * Sum of non-void modifier effective prices, using the canonical rule above.
 */
export function getModSum(item) {
  let modifiersSum = 0;
  if (item && item.modifiers) {
    item.modifiers.forEach(function (mod) {
      if (!mod.void_status) {
        modifiersSum += getModifierEffectivePrice(mod);
      }
    });
  }
  return modifiersSum;
}

/**
 * Net line subtotal for a single item: (price + mods − itemDiscount −
 * wholeDiscount) × quantity, rounded to 2dp HALF_UP. Mirrors
 * helpers/calc.js `getItemSubtotal`.
 */
export function getItemSubtotal(item) {
  if (!item) {
    throw Error("[pricing.getItemSubtotal] No item provided");
  }

  const quantity = new Decimal(item.quantity);
  const price = new Decimal(item.price || 0);
  const itemDiscount = new Decimal(item.discount || 0);

  let modSum = new Decimal(0);
  if (item.modifiers && Array.isArray(item.modifiers)) {
    item.modifiers.forEach((mod) => {
      if (!mod.void_status) {
        modSum = modSum.plus(new Decimal(getModifierEffectivePrice(mod)));
      }
    });
  }

  let unitPrice = price.plus(modSum).minus(itemDiscount);

  if (item.wholeDiscount) {
    unitPrice = unitPrice.minus(new Decimal(item.wholeDiscount));
  }

  return parseFloat(unitPrice.times(quantity).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toString());
}

/**
 * Grand total for a single item. Inclusive: subtotal already contains tax.
 * Exclusive: subtotal + item.tax. Mirrors helpers/calc.js `getItemGrandTotal`.
 */
export function getItemGrandTotal(item, isTaxInclusive) {
  if (!item) return 0;
  const subtotal = new Decimal(getItemSubtotal(item));
  if (isTaxInclusive) {
    return parseFloat(subtotal.toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toString());
  }
  const tax = new Decimal(item.tax || 0);
  return parseFloat(subtotal.plus(tax).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toString());
}

// ─── Order-level helpers ──────────────────────────────────────────────────────

/**
 * Sum of non-void item subtotals, rounded to 2dp HALF_UP. Mirrors
 * helpers/calc.js `getOrderSubTotal`.
 */
export function getOrderSubtotal(order) {
  if (!order || !order.items) return 0;
  let res = new Decimal(0);
  order.items.forEach((item) => {
    if (!item.void_status) {
      res = res.plus(new Decimal(getItemSubtotal(item)));
    }
  });
  return parseFloat(res.toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toString());
}

/**
 * Order grand total (subtotal + tax, net of discounts, EXCLUDING surcharge and
 * tips). Mirrors helpers/calc.js `getOrderGrantTotal`. For inclusive tax the
 * subtotal already contains tax.
 */
export function getOrderGrandTotal(order, isTaxInclusive) {
  const subTotal = new Decimal(getOrderSubtotal(order));
  if (isTaxInclusive) {
    return parseFloat(subTotal.toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toString());
  }
  const taxTotal = new Decimal((order && order.tax_total) || 0);
  return parseFloat(subTotal.plus(taxTotal).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toString());
}

// ─── Surcharge ────────────────────────────────────────────────────────────────

/**
 * Surcharge amount for a base amount and a percentage. Replaces the float
 * `total * (surcharge / 100)` in orderMixin.js `surchargeCharged`. Uses Decimal
 * internally (callers historically applied their own `.toFixed(2)`), so the
 * returned value is NOT pre-rounded to keep parity with the legacy call sites.
 */
export function computeSurcharge(base, percent) {
  return parseFloat(
    new Decimal(toNumber(base)).times(new Decimal(toNumber(percent)).dividedBy(100)).toString()
  );
}

// ─── Final / display total ────────────────────────────────────────────────────

/**
 * The canonical "amount the customer pays" before payments are applied:
 *   grand_total + surcharge_value + tips_total
 *
 * `grand_total` is stored by convention WITHOUT tips (see
 * store/modules/orders.js normalization), so adding tips here is correct and
 * does not double-count. This replaces every inline
 * `grand_total + surcharge_value + tips_total` formula across the app.
 *
 * Returns the raw (unrounded) sum to exactly match the legacy inline
 * expressions; display sites apply their own `.toFixed(2)`.
 */
export function getOrderFinalTotal(order) {
  if (!order) return 0;
  return (
    toNumber(order.grand_total) +
    toNumber(order.surcharge_value) +
    toNumber(order.tips_total)
  );
}

// ─── Points ───────────────────────────────────────────────────────────────────

/**
 * Reward-points context for an order. Mirrors the float math in orderMixin.js
 * `countSubtotal` (points block) and cdOrderMixin's `earned_points`.
 *
 * Phase 3: the modifier sum now uses the canonical `getModifierEffectivePrice`
 * rule (price_original ?? price), the same as every other money-math path.
 * Previously this block used `price_original || 0` and silently ignored
 * modifiers that only carried `price`.
 */
export function computePointsContext(order, settings = {}) {
  const rewardsConversionRate = settings.rewardsConversionRate || 0.01;
  const isTaxInclusive = !!settings.isTaxInclusive;

  let subtotalWithoutPoints = 0;
  let taxWithoutPoints = 0;
  const items = (order && order.items) || [];
  for (const item of items) {
    if (!item.void_status && item.name !== POINTS_ITEM_NAME) {
      const itemPrice = item.price || 0;
      const itemDiscount = item.discount || 0;
      let modSum = 0;
      if (item.modifiers && Array.isArray(item.modifiers)) {
        for (const mod of item.modifiers) {
          if (!mod.void_status) modSum += getModifierEffectivePrice(mod);
        }
      }
      subtotalWithoutPoints += (itemPrice - itemDiscount + modSum) * item.quantity;
      taxWithoutPoints += toNumber(item.tax);
    }
  }

  const grossMerch = Math.max(0, subtotalWithoutPoints);

  // A whole-order discount reduces how many points can be redeemed: cap by the
  // merchandise total AFTER that discount, not the pre-discount subtotal, so
  // points + discount can never drive the order below zero. Mirrors the
  // discount-input semantics of orderMixin.js `applyWholeOrderDiscount`
  // (discount_orders is a percentage when raw_type === 1, else a flat amount).
  const discountType = toNumber(order && order.discount_orders_raw_type);
  const discountInput = Math.max(0, toNumber(order && order.discount_orders) || 0);
  let orderDiscount = 0;
  if (discountInput > 0) {
    orderDiscount = discountType === 1
      ? grossMerch * (discountInput / 100)
      : discountInput;
  }
  const discountedMerch = Math.max(0, grossMerch - orderDiscount);

  // Points should be redeemable against the full amount the customer pays,
  // not just the pre-tax merchandise total, so add tax back on top of the
  // discounted merch value. `item.tax` is already computed on the
  // post-discount subtotal (orderMixin.js runs discounts before taxes), so it
  // is added as-is rather than re-derived from `discountedMerch`. Skipped for
  // tax-inclusive pricing, where `item.price` already has tax baked in.
  const taxComponent = isTaxInclusive ? 0 : Math.max(0, taxWithoutPoints);
  const redeemableValue = round2(discountedMerch + taxComponent);
  const maxPointsByValue = Math.floor(redeemableValue / rewardsConversionRate + 1e-9);

  const pointsItem = items.find((item) => item.name === POINTS_ITEM_NAME && !item.void_status);
  const redeemedPoints = pointsItem ? Math.abs(pointsItem.quantity) : 0;
  const redeemedValue = pointsItem ? toNumber(pointsItem.quantity) * toNumber(pointsItem.price) : 0;

  const earnedPoints = Math.floor(toNumber(order && order.grand_total) || 0);

  return {
    conversionRate: rewardsConversionRate,
    subtotalWithoutPoints,
    redeemableValue,
    maxPointsByValue,
    redeemedPoints,
    redeemedValue,
    earnedPoints,
  };
}

// ─── Primary API ──────────────────────────────────────────────────────────────

/**
 * Compute a normalized totals object for an order. This is the single source of
 * truth that the order screen, checkout, receipts and customer display should
 * consume.
 *
 * When `order.items` is present and non-empty, subtotal / grand total are
 * recomputed from line items (authoritative). Otherwise the engine falls back
 * to the order's stored aggregate fields (e.g. server-fetched orders that only
 * carry totals).
 *
 * @param {object} order
 * @param {object} settings { isTaxInclusive, taxRound, rewardsConversionRate }
 * @returns {object} normalized totals
 */
export function computeOrderTotals(order, settings = {}) {
  const safeOrder = order || {};
  const { isTaxInclusive = false, taxRound = TAX_ROUND.NEAREST } = settings;
  const hasItems = Array.isArray(safeOrder.items) && safeOrder.items.length > 0;

  const subtotal = hasItems ? getOrderSubtotal(safeOrder) : toNumber(safeOrder.subtotal);
  const taxTotal = roundMoney(safeOrder.tax_total, taxRound);
  const grandTotal = hasItems
    ? getOrderGrandTotal(safeOrder, isTaxInclusive)
    : toNumber(safeOrder.grand_total);

  const discountTotal = round2(safeOrder.discount_total);
  const orderDiscount = round2(safeOrder.order_discount);
  const surcharge = round2(safeOrder.surcharge_value);
  const tips = round2(safeOrder.tips_total);

  // Use stored grand_total for the customer-facing final total so display
  // exactly matches the legacy inline formula; fall back to the recomputed
  // value when the order has no stored grand_total.
  const displayGrandTotal =
    safeOrder.grand_total !== undefined && safeOrder.grand_total !== null
      ? toNumber(safeOrder.grand_total)
      : grandTotal;

  const finalTotalRaw = displayGrandTotal + surcharge + tips;
  const finalTotal = round2(finalTotalRaw);

  const points = computePointsContext(safeOrder, settings);

  return {
    subtotal,
    discountTotal,
    orderDiscount,
    taxTotal,
    grandTotal: displayGrandTotal,
    computedGrandTotal: grandTotal,
    surcharge,
    tips,
    finalTotal,
    finalTotalRaw,
    amountDue: finalTotal,
    points,
    settings: { isTaxInclusive, taxRound, rewardsConversionRate: points.conversionRate },
  };
}
